import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useToolsContext } from '../../context/ToolsContext';
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext';
import Tool from '../Tool/Tool';
import Modal from '../Bootstrap/Modal/Modal';
import './ToolboxMenuItems.css';

/** Tiempo para mover el cursor del disparador al popover sin que se cierre */
const POPOVER_HIDE_DELAY_MS = 750;
const POPOVER_SHOW_DELAY_MS = 80;
/** Separación respecto al borde de la ventana (Popper preventOverflow) */
const VIEWPORT_PADDING_PX = 16;

/**
 * Popper: mantener el contenido dentro del viewport y voltear si no hay hueco.
 * strategy fixed evita cortes con padres overflow:hidden y canvas del mapa.
 */
function getToolboxPopperConfig() {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    return {
        strategy: 'fixed',
        modifiers: [
            { name: 'offset', options: { offset: [0, 6] } },
            {
                name: 'flip',
                options: {
                    padding: VIEWPORT_PADDING_PX,
                    fallbackPlacements: ['right', 'left', 'top', 'bottom']
                }
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: root || 'clippingParents',
                    altBoundary: true,
                    padding: VIEWPORT_PADDING_PX
                }
            }
        ]
    };
}

/** Mismo id en todas las sesiones para no colisionar con otras herramientas */
const OVERFLOW_TOOL_ID = 'toolboxMenuItems_overflow';

function getToolboxSection(config) {
    const section = config?.TOOLS?.toolboxMenuItems;
    return section && typeof section === 'object' ? section : null;
}

function getItemPayload(item) {
    const type = item?.type || 'link';
    const raw = item?.data?.[type];
    return raw && typeof raw === 'object' ? raw : {};
}

/** Leyendas / contenido HTML: hover, foco o clic para abrir el popover */
const POPOVER_TRIGGERS = ['hover', 'focus', 'click'];

/**
 * OverlayTrigger inyecta en el Popover ref, estilo y atributos de Popper.
 * Sin {...popoverProps} el overlay no llega a mostrarse / posicionarse.
 */
function renderPopoverOverlay(item, popoverProps = {}) {
    const d = getItemPayload(item);
    const title = d.title || '';
    const html = d.content || '';

    return (
        <Popover
            {...popoverProps}
            id={popoverProps.id || `toolbox-popover-${item.id}`}
            className={['toolbox-legend-popover shadow', popoverProps.className].filter(Boolean).join(' ')}
            style={{
                ...popoverProps.style,
                maxWidth: 'min(calc(100vw - 2rem), max(26rem, 60vw))',
                maxHeight: '50vh',
                zIndex: 1070
            }}
        >
            {title ? (
                <Popover.Header as="div" className="py-2 small fw-semibold">
                    {title}
                </Popover.Header>
            ) : null}
            <Popover.Body
                className="p-2 small toolbox-legend-body-scroll"
                style={{
                    overflow: 'auto',
                    overflowX: 'hidden',
                    maxHeight: 'min(68dvh, 28rem)'
                }}
            >
                {html ? (
                    <div className="toolbox-legend-content" dangerouslySetInnerHTML={{ __html: html }} />
                ) : (
                    <span className="text-muted fst-italic">Sin contenido</span>
                )}
            </Popover.Body>
        </Popover>
    );
}

export default function ToolboxMenuItems() {
    const { t } = useTranslation();
    const { config } = useGlobalVariablesContext();
    const { setToolState } = useToolsContext();

    /** modal | iframe — popover usa OverlayTrigger, no estado modal */
    const [overlay, setOverlay] = useState(null);

    const toolboxCfg = config ? getToolboxSection(config) : null;

    const visibleItems = useMemo(() => {
        const section = getToolboxSection(config);
        const items = section?.params?.items;
        if (!Array.isArray(items)) return [];
        return items.filter((it) => it && it.visible !== false);
    }, [config]);

    if (!config || !toolboxCfg || toolboxCfg.enable === false) {
        return null;
    }

    if (visibleItems.length === 0) {
        return null;
    }

    const closeOverflowMenu = () => {
        setToolState(OVERFLOW_TOOL_ID, {
            active: false,
            sidebarOpen: false,
            type: 'toggleable'
        });
    };

    const openOverlay = (kind, item) => {
        setOverlay({ kind, item });
    };

    const closeOverlay = () => setOverlay(null);

    const runItemAction = (item, { closeOverflow } = {}) => {
        if (item.disabled) return;

        switch (item.type) {
            case 'link': {
                const d = getItemPayload(item);
                const url = d.url || '';
                const target = d.target === '_self' ? '_self' : '_blank';
                if (!url) return;
                if (target === '_self') {
                    window.location.assign(url);
                } else {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
                break;
            }
            case 'modal':
                openOverlay('modal', item);
                break;
            case 'modal-iframe':
                openOverlay('iframe', item);
                break;
            case 'popover':
                /* El contenido se muestra en OverlayTrigger + Popover */
                break;
            default:
                break;
        }

        if (closeOverflow) {
            closeOverflowMenu();
        }
    };

    const renderToolbarButton = (item) => {
        const title = item.tooltip || item.label || '';
        const iconClass = item.icon || 'bi bi-menu-app';
        const hasBadge = item.badge?.label;

        if (item.type === 'popover') {
            const trigger = (
                <span
                    className={`list-item ${item.disabled ? 'opacity-50' : ''}`}
                    style={{ cursor: item.disabled ? 'not-allowed' : 'pointer' }}
                    title={title}
                    role="button"
                    tabIndex={item.disabled ? -1 : 0}
                    aria-label={item.label || title}
                >
                    <i className={iconClass} aria-hidden="true" />
                    {hasBadge && (
                        <span className={`badge ${item.badge?.color || 'bg-primary'} ms-1`} style={{ fontSize: '0.65rem' }}>
                            {item.badge.label}
                        </span>
                    )}
                </span>
            );

            if (item.disabled) {
                return <React.Fragment key={item.id}>{trigger}</React.Fragment>;
            }

            return (
                <OverlayTrigger
                    key={item.id}
                    trigger={POPOVER_TRIGGERS}
                    placement="auto"
                    delay={{ show: POPOVER_SHOW_DELAY_MS, hide: POPOVER_HIDE_DELAY_MS }}
                    container={typeof document !== 'undefined' ? document.body : null}
                    popperConfig={getToolboxPopperConfig()}
                    overlay={(props) => renderPopoverOverlay(item, props)}
                >
                    {trigger}
                </OverlayTrigger>
            );
        }

        return (
            <span
                key={item.id}
                className={`list-item ${item.disabled ? 'opacity-50' : ''}`}
                onClick={() => runItemAction(item)}
                style={{ cursor: item.disabled ? 'not-allowed' : 'pointer' }}
                title={title}
                role="button"
                aria-label={item.label || title}
            >
                <i className={iconClass} aria-hidden="true" />
                {hasBadge && (
                    <span className={`badge ${item.badge?.color || 'bg-primary'} ms-1`} style={{ fontSize: '0.65rem' }}>
                        {item.badge.label}
                    </span>
                )}
            </span>
        );
    };

    const renderOverflowSidebarList = () => (
        <div className="toolbox-menu-items-overflow">
            {visibleItems.map((item) => {
                const title = item.tooltip || item.label || '';
                const iconClass = item.icon || 'bi bi-menu-app';

                if (item.type === 'popover') {
                    const inner = (
                        <button
                            type="button"
                            className="btn btn-link text-start text-decoration-none w-100 py-2 border-bottom rounded-0 d-flex align-items-center gap-2"
                            disabled={!!item.disabled}
                            title={title}
                        >
                            <i className={iconClass} aria-hidden="true" />
                            <span className="flex-grow-1">{item.label || title}</span>
                            {item.badge?.label && (
                                <span className={`badge ${item.badge.color || 'bg-primary'}`}>{item.badge.label}</span>
                            )}
                        </button>
                    );

                    if (item.disabled) {
                        return <div key={item.id}>{inner}</div>;
                    }

                    return (
                        <OverlayTrigger
                            key={item.id}
                            trigger={POPOVER_TRIGGERS}
                            placement="auto"
                            delay={{ show: POPOVER_SHOW_DELAY_MS, hide: POPOVER_HIDE_DELAY_MS }}
                            container={typeof document !== 'undefined' ? document.body : null}
                            popperConfig={getToolboxPopperConfig()}
                            overlay={(props) => renderPopoverOverlay(item, props)}
                        >
                            {inner}
                        </OverlayTrigger>
                    );
                }

                return (
                    <button
                        key={item.id}
                        type="button"
                        className="btn btn-link text-start text-decoration-none w-100 py-2 border-bottom rounded-0 d-flex align-items-center gap-2"
                        disabled={!!item.disabled}
                        onClick={() => runItemAction(item, { closeOverflow: true })}
                        title={title}
                    >
                        <i className={iconClass} aria-hidden="true" />
                        <span className="flex-grow-1">{item.label || title}</span>
                        {item.badge?.label && (
                            <span className={`badge ${item.badge.color || 'bg-primary'}`}>{item.badge.label}</span>
                        )}
                    </button>
                );
            })}
        </div>
    );

    const renderOverlay = () => {
        if (!overlay) return null;

        const { kind, item } = overlay;

        if (kind === 'modal') {
            const d = getItemPayload(item);
            const bodyHtml = d.content || '';
            const footerText = d.footer || '';
            return (
                <Modal
                    show
                    onClose={closeOverlay}
                    id={`toolbox-modal-${item.id}`}
                    title={d.title || ''}
                    footer={
                        footerText ? (
                            <span className="text-muted small" dangerouslySetInnerHTML={{ __html: footerText }} />
                        ) : null
                    }
                >
                    <div className="toolbox-menu-modal-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                </Modal>
            );
        }

        if (kind === 'iframe') {
            const d = getItemPayload(item);
            const url = d.url || '';
            return (
                <Modal
                    show
                    onClose={closeOverlay}
                    id={`toolbox-iframe-${item.id}`}
                    title={d.title || ''}
                    width="full"
                    footer=""
                    className="modal-iframe"
                >
                    {url ? (
                        <iframe
                            title={d.title || 'content'}
                            src={url}
                            style={{ width: '100%', flex: 1, border: 0 }}
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        />
                    ) : (
                        <p className="text-muted small mb-0">{t('No URL configured')}</p>
                    )}
                </Modal>
            );
        }

        return null;
    };

    /* Más de 3 ítems: un único botón tipo MoreTools que abre el submenú offcanvas */
    if (visibleItems.length > 3) {
        return (
            <>
                <Tool
                    id={OVERFLOW_TOOL_ID}
                    type="toggleable"
                    icon="bi bi-menu-app"
                    label={t('Toolbox menu')}
                    sidebarTitle={t('Toolbox menu')}
                    hasMoreTools
                    sidebarMoreTools={renderOverflowSidebarList()}
                />
                {renderOverlay()}
            </>
        );
    }

    /* Hasta 3 ítems: un botón de barra por ítem */
    return (
        <>
            {visibleItems.map((item) => renderToolbarButton(item))}
            {renderOverlay()}
        </>
    );
}
